import { useState } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import {
  createPaymentLink,
  getCreditValidator,
} from "../hooks/local/userReducer";

import { apiEndPoints } from "../hooks/remote/apiEndPoints";

import close from "../assets/svg-icons/closeBrand.svg";

const PaymentModal = ({
  isOpen,
  onClose,
  currency,
  totalPrice,
  virtualAccount,
  onPaymentSuccess,
}) => {
  const dispatch = useDispatch();

  // ============================================================
  // EXISTING PAYMENT CONFIRMATION
  // ============================================================

  const [
    paymentConfirmed,
    setPaymentConfirmed,
  ] = useState(false);

  const [
    confirmPaymentLoading,
    setConfirmPaymentLoading,
  ] = useState(false);

  const [
    retryCount,
    setRetryCount,
  ] = useState(0);

  const [
    showSupportModal,
    setShowSupportModal,
  ] = useState(false);

  // ============================================================
  // EXISTING USD PAYMENT LINK
  // ============================================================

  const [
    paymentLink,
    setPaymentLink,
  ] = useState("");

  const [
    linkGenerated,
    setLinkGenerated,
  ] = useState(false);

  const [
    amountValue,
    setAmountValue,
  ] = useState("");

  // ============================================================
  // NEW SQUAD PAYMENT LINK
  // ============================================================

  const [
    squadPaymentLoading,
    setSquadPaymentLoading,
  ] = useState(false);

  // ============================================================
  // ACCOUNT DETAILS
  // ============================================================

  const permanentAccountNumber = String(
    virtualAccount?.acct_num || "",
  ).trim();

  const hasPermanentVirtualAccount =
    permanentAccountNumber.length > 0;

  // ============================================================
  // EXISTING USD PAYMENT LINK
  // ============================================================

  const dollarPaymentLink =
    useFormik({
      initialValues: {
        amount: amountValue,
      },

      validationSchema:
        Yup.object({
          amount:
            Yup.string().required(
              "Amount cannot be empty",
            ),
        }),

      onSubmit: async (
        values,
        { resetForm },
      ) => {
        try {
          const {
            amount,
          } = values;

          const {
            payload,
          } = await dispatch(
            createPaymentLink({
              amount,
            }),
          );

          if (
            payload?.statuscode !==
            "00"
          ) {
            toast.error(
              payload?.statusmessage ||
                "Unable to generate payment link.",
            );

            return;
          }

          const generatedPaymentLink =
            payload?.payment_link;

          if (
            !generatedPaymentLink
          ) {
            toast.error(
              "Payment link was not returned.",
            );

            return;
          }

          setPaymentLink(
            generatedPaymentLink,
          );

          setLinkGenerated(true);

          resetForm();

          setAmountValue("");
        } catch (error) {
          toast.error(
            error?.message ||
              "Unable to generate payment link.",
          );
        }
      },
    });

  const handleAmountChange = (
    event,
  ) => {
    const value =
      event.target.value;

    setAmountValue(value);

    dollarPaymentLink.setFieldValue(
      "amount",
      value,
    );
  };

  // ============================================================
  // NEW SQUAD PAYMENT LINK
  // ============================================================

  const handleGenerateSquadPaymentLink =
    async () => {
      const amount =
        Number(totalPrice);

      if (
        !amount ||
        amount <= 0
      ) {
        toast.error(
          "Resource amount is unavailable.",
        );

        return;
      }

      try {
        setSquadPaymentLoading(
          true,
        );

        const response =
          await apiEndPoints.generateSquadPaymentLink(
            {
              amount,
            },
          );

        /*
         * Support:
         * response.data.data
         * response.data
         * response
         */
        const data =
          response?.data?.data ||
          response?.data ||
          response;


        // ======================================================
        // CHECK BACKEND STATUS
        // ======================================================

        if (
          data?.statuscode !==
          "00"
        ) {
          toast.error(
            data?.statusmessage ||
              data?.message ||
              "Payment link could not be generated.",
          );

          return;
        }

        /*
         * Actual successful response:
         *
         * {
         *   payment_link: {
         *     data: {
         *       checkout_url:
         *         "https://pay.squadco.com/...",
         *       callback_url: "...",
         *       transaction_ref:
         *         "SQPAY_xxx",
         *       ...
         *     },
         *     message: "Success",
         *     status: 200,
         *     success: true
         *   },
         *   statuscode: "00",
         *   statusmessage: "successful"
         * }
         */

        const paymentUrl =
          data?.payment_link?.data
            ?.checkout_url;

        if (
          !paymentUrl ||
          typeof paymentUrl !==
            "string"
        ) {
          console.error(
            "Squad checkout URL missing:",
            data,
          );

          toast.error(
            "Payment link was not returned.",
          );

          return;
        }

        // ======================================================
        // SAVE PAYMENT CONTEXT
        // ======================================================

        /*
         * This payment was started while
         * purchasing a resource.
         *
         * We don't need to store the
         * transaction reference here.
         *
         * After payment SQuAD/backend
         * redirects to:
         *
         * /client-area?reference=SQPAY_xxx
         *
         * Dashboard handles verification.
         */

        sessionStorage.setItem(
          "pendingSquadPaymentAmount",
          String(amount),
        );

        sessionStorage.setItem(
          "pendingSquadPaymentType",
          "resource_purchase",
        );

        sessionStorage.setItem(
          "pendingSquadReturnPath",
          `${window.location.pathname}${window.location.search}`,
        );

        // ======================================================
        // REDIRECT TO SQUAD CHECKOUT
        // ======================================================

        window.location.assign(
          paymentUrl,
        );
      } catch (error) {
        console.error(
          "Squad payment link generation error:",
          error,
        );

        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            "Unable to generate Squad payment link.",
        );
      } finally {
        setSquadPaymentLoading(
          false,
        );
      }
    };

  // ============================================================
  // EXISTING PERMANENT ACCOUNT CONFIRMATION
  // ============================================================

  const handleConfirmPayment =
    async () => {
      if (!paymentConfirmed) {
        toast.error(
          "Please confirm that you have made the payment.",
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: true,
            theme: "colored",
          },
        );

        return;
      }

      setConfirmPaymentLoading(
        true,
      );

      try {
        const {
          payload,
        } = await dispatch(
          getCreditValidator({
            amount:
              Number(totalPrice),
          }),
        );

        if (
          payload?.statuscode !==
          "00"
        ) {
          throw new Error(
            "Payment verification failed",
          );
        }

        const userBalance =
          payload?.pref_deno ===
          "naira"
            ? Number(
                payload?.balance ||
                  0,
              )
            : Number(
                payload
                  ?.dollar_balance ||
                  0,
              );

        if (
          userBalance <
          Number(totalPrice)
        ) {
          const nextRetry =
            retryCount + 1;

          setRetryCount(
            nextRetry,
          );

          if (
            nextRetry >= 2
          ) {
            setShowSupportModal(
              true,
            );
          } else {
            toast.error(
              `Payment unsuccessful. Insufficient balance: ${
                currency ===
                "USD"
                  ? "USD"
                  : "₦"
              } ${userBalance.toLocaleString()}. Required: ${
                currency ===
                "USD"
                  ? "USD"
                  : "₦"
              } ${Number(
                totalPrice,
              ).toLocaleString()}.`,
              {
                position:
                  "top-right",
                autoClose: 5000,
                hideProgressBar:
                  true,
                theme: "colored",
              },
            );
          }

          return;
        }

        setShowSupportModal(
          false,
        );

        setRetryCount(0);

        setPaymentConfirmed(
          false,
        );

        setLinkGenerated(false);

        setPaymentLink("");

        toast.success(
          "Payment confirmed successfully!",
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: true,
            theme: "colored",
          },
        );

        if (
          typeof onPaymentSuccess ===
          "function"
        ) {
          onPaymentSuccess();
        }

        onClose();
      } catch (error) {
        const nextRetry =
          retryCount + 1;

        setRetryCount(
          nextRetry,
        );

        if (
          nextRetry >= 2
        ) {
          setShowSupportModal(
            true,
          );
        } else {
          toast.error(
            error?.message ||
              "Payment verification failed. Please try again.",
            {
              position:
                "top-right",
              autoClose: 3000,
              hideProgressBar:
                true,
              theme: "colored",
            },
          );
        }
      } finally {
        setConfirmPaymentLoading(
          false,
        );
      }
    };

  // ============================================================
  // CLOSE PAYMENT MODAL
  // ============================================================

  const handleClosePaymentModal =
    () => {
      setPaymentConfirmed(
        false,
      );

      setConfirmPaymentLoading(
        false,
      );

      setRetryCount(0);

      setPaymentLink("");

      setLinkGenerated(false);

      setAmountValue("");

      setSquadPaymentLoading(
        false,
      );

      onClose();
    };

  const handleSupportModalClose =
    () => {
      setShowSupportModal(
        false,
      );

      handleClosePaymentModal();
    };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* ===================================================== */}
      {/* PAYMENT MODAL */}
      {/* ===================================================== */}

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">

        <div className="max-h-[80vh] w-full max-w-md overflow-auto rounded-lg bg-white dark:bg-[#141D26]">

          {/* HEADER */}

          <div className="flex items-center justify-between border-b border-[#c4c4c432] p-6 dark:border-[#c4c4c432]">

            <h2 className="text-sm font-medium text-primaryBlue">
              {currency === "USD"
                ? "Dollar"
                : "Naira"}{" "}
              Account Details
            </h2>

            <img
              src={close}
              onClick={
                handleClosePaymentModal
              }
              className="h-5 cursor-pointer"
              alt="Close"
            />

          </div>

          <div className="grid gap-4 p-6 text-sm">

            {/* ================================================= */}
            {/* PERMANENT ACCOUNT USERS */}
            {/* ================================================= */}

            {hasPermanentVirtualAccount ? (
              <>

                {currency === "USD" ? (

                  linkGenerated ? (

                    <div className="grid gap-4">

                      <input
                        type="text"
                        value={
                          paymentLink
                        }
                        disabled
                        className="w-full rounded-md border border-[#c4c4c450] bg-gray-100 p-3 text-gray-500 dark:border-[#c4c4c416] dark:bg-gray-700 dark:text-gray-300"
                      />

                      {paymentLink && (

                        <a
                          href={
                            paymentLink
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >

                          <button
                            type="button"
                            className="w-full rounded bg-primaryBlue p-4 text-xs text-white hover:bg-blue-600"
                          >
                            Make Payment
                          </button>

                        </a>

                      )}

                    </div>

                  ) : (

                    <>
                      {/* USD OPTION 1 */}

                      <div className="grid gap-2">

                        <p className="font-semibold">
                          OPTION 1:
                          Generate Payment
                          Link
                        </p>

                        <div>

                          <input
                            type="text"
                            placeholder="Enter amount"
                            value={
                              amountValue
                            }
                            onChange={
                              handleAmountChange
                            }
                            onBlur={
                              dollarPaymentLink
                                .handleBlur
                            }
                            className="w-full rounded-md border border-[#c4c4c450] bg-white p-3 text-black focus:outline-none focus:ring-2 focus:ring-primaryBlue dark:border-[#c4c4c416] dark:bg-[#1f2a44] dark:text-white"
                          />

                          {dollarPaymentLink
                            .errors
                            .amount &&
                            dollarPaymentLink
                              .touched
                              .amount && (

                              <span className="mt-1 text-xs text-red-500">
                                {
                                  dollarPaymentLink
                                    .errors
                                    .amount
                                }
                              </span>

                            )}

                        </div>

                        <button
                          type="button"
                          className="w-full rounded bg-primaryBlue p-4 text-xs text-white hover:bg-blue-600"
                          onClick={(
                            event,
                          ) => {
                            event.preventDefault();

                            dollarPaymentLink.handleSubmit(
                              event,
                            );
                          }}
                        >
                          Generate Payment
                          Link
                        </button>

                      </div>

                      {/* USD OPTION 2 */}

                      <div className="mt-5 grid gap-2 border-t border-[#c4c4c432] pt-5">

                        <p className="font-semibold">
                          OPTION 2:
                          Bank Transfer
                        </p>

                        <p>
                          Transfer the exact
                          amount to the
                          account below and
                          call{" "}

                          <a
                            href="tel:09069434255"
                            className="text-blue-500 underline"
                          >
                            09069434255
                          </a>

                          {" "}or send a
                          payment receipt to{" "}

                          <a
                            href="mailto:support@cloudvantage.com.ng"
                            className="text-blue-500 underline"
                          >
                            support@cloudvantage.com.ng
                          </a>
                          .
                        </p>

                        <p>
                          <span className="font-semibold">
                            Bank name:
                          </span>{" "}
                          Guaranty Trust
                          Bank
                        </p>

                        <p>
                          <span className="font-semibold">
                            Account number:
                          </span>{" "}
                          0891825462
                        </p>

                        <p>
                          <span className="font-semibold">
                            Account name:
                          </span>{" "}
                          Cloudvantage
                          Limited
                        </p>

                      </div>
                    </>

                  )

                ) : (

                  /* ============================================= */
                  /* PERMANENT NAIRA ACCOUNT */
                  /* ============================================= */

                  <div className="grid gap-2">

                    <p className="mb-5 font-semibold">
                      Fund your wallet by
                      making a bank transfer
                      to the account details
                      below:
                    </p>

                    <p>
                      <span className="font-semibold">
                        Bank name:
                      </span>{" "}
                      Providus Bank
                    </p>

                    <p>
                      <span className="font-semibold">
                        Account number:
                      </span>{" "}
                      {
                        permanentAccountNumber
                      }
                    </p>

                    <p>
                      <span className="font-semibold">
                        Account name:
                      </span>{" "}
                      {virtualAccount
                        ?.acct_name ||
                        ""}
                    </p>

                  </div>

                )}

                {/* CONFIRM EXISTING BANK TRANSFER */}

                {!linkGenerated && (

                  <>
                    <label className="mt-4 flex items-center gap-2">

                      <input
                        type="checkbox"
                        checked={
                          paymentConfirmed
                        }
                        disabled={
                          confirmPaymentLoading
                        }
                        onChange={(
                          event,
                        ) =>
                          setPaymentConfirmed(
                            event.target
                              .checked,
                          )
                        }
                        className="h-5 w-5 rounded border-gray-300 text-primaryBlue focus:ring-primaryBlue"
                      />

                      <span>
                        I can confirm I
                        already made payment
                      </span>

                    </label>

                    <button
                      type="button"
                      className={`flex w-full items-center justify-center rounded p-4 text-xs text-white ${
                        retryCount >= 2 ||
                        confirmPaymentLoading
                          ? "cursor-not-allowed bg-gray-500"
                          : "bg-primaryBlue hover:bg-blue-600"
                      }`}
                      onClick={
                        handleConfirmPayment
                      }
                      disabled={
                        retryCount >= 2 ||
                        confirmPaymentLoading
                      }
                    >

                      {confirmPaymentLoading
                        ? "Confirming payment..."
                        : retryCount > 0
                          ? "Retry Payment"
                          : "Confirm Payment"}

                    </button>
                  </>

                )}

              </>

            ) : currency === "USD" ? (

              /* ================================================= */
              /* NO PERMANENT ACCOUNT + USD */
              /* ================================================= */

              <div className="grid gap-4">

                <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">

                  <p className="font-semibold">
                    Payment unavailable
                  </p>

                  <p className="mt-1 text-xs">
                    Squad payment is
                    currently available for
                    Naira payments only.
                  </p>

                </div>

                <p className="text-gray-500">
                  Switch your wallet
                  currency to Naira to
                  continue.
                </p>

              </div>

            ) : (

              /* ================================================= */
              /* NEW SQUAD PAYMENT LINK */
              /* ================================================= */

              <div className="grid gap-4">

                <div className="rounded-md border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">

                  <p className="font-semibold text-blue-800 dark:text-blue-300">
                    Pay with Squad
                  </p>

                  <p className="mt-1 text-xs text-blue-700 dark:text-blue-400">
                    Your wallet balance is
                    insufficient for this
                    resource. You will be
                    redirected to
                    Squad&apos;s secure
                    payment page.
                  </p>

                </div>

                {/* RESOURCE PRICE */}

                <div className="grid gap-2">

                  <label className="font-semibold">
                    Resource amount
                  </label>

                  <input
                    type="text"
                    value={`₦${Number(
                      totalPrice || 0,
                    ).toLocaleString()}`}
                    disabled
                    className="w-full rounded-md border bg-gray-100 p-3 dark:bg-gray-700"
                  />

                  <p className="text-xs text-gray-500">
                    This amount is fixed
                    and cannot be edited.
                  </p>

                </div>

                {/* PAYMENT INFORMATION */}

                <div className="rounded-md border border-gray-200 p-4 dark:border-gray-700">

                  <p className="text-xs text-gray-500 dark:text-gray-300">
                    After completing your
                    payment, you will be
                    redirected back to
                    CloudVantage. Your
                    payment will be
                    verified automatically
                    and your wallet balance
                    will be updated.
                  </p>

                </div>

                {/* PROCEED TO SQUAD */}

                <button
                  type="button"
                  className="w-full rounded bg-primaryBlue p-4 text-xs text-white disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={
                    squadPaymentLoading ||
                    Number(
                      totalPrice,
                    ) <= 0
                  }
                  onClick={
                    handleGenerateSquadPaymentLink
                  }
                >

                  {squadPaymentLoading
                    ? "Generating payment link..."
                    : "Proceed to Payment"}

                </button>

              </div>

            )}

          </div>
        </div>
      </div>

      {/* ===================================================== */}
      {/* SUPPORT MODAL */}
      {/* ===================================================== */}

      {showSupportModal && (

        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">

          <div className="w-full max-w-md rounded-lg bg-white dark:bg-[#141D26]">

            <div className="p-6">

              <h2 className="mb-4 text-lg font-semibold text-primaryBlue">
                Payment Verification
                Failed
              </h2>

              <p className="text-sm">
                We could not verify your
                payment. Please contact
                our support team at{" "}

                <a
                  href="tel:09069434255"
                  className="text-blue-500 underline"
                >
                  09069434255
                </a>

                {" "}or email{" "}

                <a
                  href="mailto:support@cloudvantage.com.ng"
                  className="text-blue-500 underline"
                >
                  support@cloudvantage.com.ng
                </a>

                {" "}to confirm your
                payment.
              </p>

              <div className="mt-4 flex justify-end">

                <button
                  type="button"
                  className="rounded bg-primaryBlue p-4 text-xs text-white hover:bg-blue-600"
                  onClick={
                    handleSupportModalClose
                  }
                >
                  Close
                </button>

              </div>

            </div>

          </div>

        </div>

      )}

    </>
  );
};

export default PaymentModal;